import React from 'react';
import { Text, Skeleton } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import {
    IconCurrencyDollar,
    IconActivity,
    IconUsers,
    IconHierarchy3,
    IconCircleCheck,
    IconCircleX,
} from '@tabler/icons-react';
import type { CostSummaryViewModel } from '@models/cost';
import type { ProcessSummaryViewModel } from '@models/monitoring';
import { formatCurrency, formatNumber } from '@utils/formatters';
import './CostSummaryCards.scss';

interface CostSummaryCardsProps {
    data: CostSummaryViewModel | null;
    isLoading: boolean;
    processData?: ProcessSummaryViewModel | null;
    showProcessCards?: boolean;
}

const CostSummaryCards: React.FC<CostSummaryCardsProps> = ({
    data,
    isLoading,
    processData,
    showProcessCards = false,
}) => {
    const cards = [
        {
            title: 'Total Cost',
            value: data ? formatCurrency(data.totalCost, data.currency) : '-',
            icon: <IconCurrencyDollar size={24} />,
            color: 'var(--mantine-color-brand-6)',
            bg: 'var(--mantine-color-brand-0)',
        },
        {
            title: 'Total Events',
            value: data ? formatNumber(data.eventCount) : '-',
            icon: <IconActivity size={24} />,
            color: 'var(--mantine-color-blue-6)',
            bg: 'var(--mantine-color-blue-0)',
        },
        {
            title: 'Unique Users',
            value: data ? formatNumber(data.usersCount) : '-',
            icon: <IconUsers size={24} />,
            color: 'var(--mantine-color-teal-6)',
            bg: 'var(--mantine-color-teal-0)',
        },
        ...(showProcessCards
            ? [
                {
                    title: 'Total Processes',
                    value: processData
                        ? formatNumber(processData.totalProcesses)
                        : '-',
                    icon: <IconHierarchy3 size={24} />,
                    color: 'var(--mantine-color-brand-6)',
                    bg: 'var(--mantine-color-brand-0)',
                },
                {
                    title: 'Completed',
                    value: processData
                        ? formatNumber(processData.completedProcesses)
                        : '-',
                    icon: <IconCircleCheck size={24} />,
                    color: 'var(--mantine-color-green-6)',
                    bg: 'var(--mantine-color-green-0)',
                },
                {
                    title: 'Failed',
                    value: processData
                        ? formatNumber(processData.failedProcesses)
                        : '-',
                    icon: <IconCircleX size={24} />,
                    color: 'var(--mantine-color-red-6)',
                    bg: 'var(--mantine-color-red-0)',
                }
            ]
            : []),
    ];

    return (
        <div className="cost-summary-grid">
            {cards.map((card) => (
                <Card key={card.title} className="cost-summary-card" withBorder>
                    {isLoading ? (
                        <div className="card-content">
                            <Skeleton circle height={48} />
                            <div style={{ flex: 1 }}>
                                <Skeleton height={14} width="60%" mb={8} />
                                <Skeleton height={24} width="40%" />
                            </div>
                        </div>
                    ) : (
                        <div className="card-content">
                            <div
                                className="icon-container"
                                style={{ backgroundColor: card.bg, color: card.color }}
                            >
                                {card.icon}
                            </div>
                            <div className="text-content">
                                <Text size="sm" c="dimmed" className="card-title">
                                    {card.title}
                                </Text>
                                <Text size="xl" className="card-value">
                                    {card.value}
                                </Text>
                            </div>
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
};

export default CostSummaryCards;
